/**
 * Inline tag editing in the profile header.
 *
 * A popover of the active tags with the contact's own ticked, rather than a
 * dialog: tagging is a one-click judgement made while reading the profile, and
 * a modal for it would be heavier than the decision.
 *
 * Only *active* tags can be added; a tag deactivated after it was applied stays
 * on the contact and stays visible, because deactivation is not deletion
 * (CLAUDE.md §5). Tag colours come from the API — the one legitimate place a
 * colour value reaches app code, since admins choose them (client CLAUDE.md).
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Spinner, TagChip } from '@meshhub/design-tokens/ui';
import { Plus } from 'lucide-react';
import React, { useState } from 'react';
import { ADMIN_TAGS_QUERY, TAG_CONTACT, UNTAG_CONTACT } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { CheckboxField, FormDialog, MutationError } from '../FormKit.web.jsx';

export function TagPicker({ contact, onSaved, readOnly }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const { data, loading } = useQuery(ADMIN_TAGS_QUERY, {
    variables: { includeInactive: false },
    skip: !open,
  });
  const [tagContact, { loading: tagging }] = useMutation(TAG_CONTACT);
  const [untagContact, { loading: untagging }] = useMutation(UNTAG_CONTACT);

  const applied = new Set(contact.tags.map((t) => t.id));

  async function toggle(tagId) {
    setError(null);
    try {
      const mutate = applied.has(tagId) ? untagContact : tagContact;
      await mutate({ variables: { contactId: contact.id, tagId } });
      await onSaved?.();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <>
      <span className="flex flex-wrap items-center gap-1">
        {contact.tags.map((tag) => (
          <TagChip key={tag.id} name={tag.name} colorHex={tag.colorHex} />
        ))}
        {!readOnly && (
          <Button
            variant="ghost"
            onClick={() => setOpen(true)}
            aria-label={S.contacts.detail.addTag}
            title={S.contacts.detail.addTag}
          >
            <Plus size={14} aria-hidden="true" />
          </Button>
        )}
      </span>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={S.contacts.detail.tags}
        onSubmit={() => setOpen(false)}
        submitLabel={S.app.close}
        busy={tagging || untagging}
      >
        <MutationError error={error} />
        {loading && <Spinner />}
        <div className="flex flex-col gap-2">
          {(data?.tags ?? []).map((tag) => (
            <CheckboxField
              key={tag.id}
              label={<TagChip name={tag.name} colorHex={tag.colorHex} />}
              checked={applied.has(tag.id)}
              onChange={() => toggle(tag.id)}
            />
          ))}
        </div>
      </FormDialog>
    </>
  );
}
